/**
 * Shared Redux Slice Utilities
 * 
 * Provides standardized patterns for Redux slices including:
 * - BaseSliceState<T> interface for consistent state shape
 * - Edit tracking reducers (markFieldAsEdited, clearEditedFields, etc.)
 * - SessionStorage backup manager for HMR recovery
 * - Generic array item reducers (add, update, remove)
 * - Priority-based ordering reducers (moveUp, moveDown)
 */

import { Draft, PayloadAction, CaseReducer } from '@reduxjs/toolkit';

// =============================================================================
// BASE STATE INTERFACE
// =============================================================================

/**
 * Standard state shape for all entity slices.
 * Use this as the base for slice state interfaces.
 * 
 * @example
 * interface WriteoffSliceState extends BaseSliceState<WriteoffView> {
 *   // Additional state properties specific to writeoffs
 * }
 */
export interface BaseSliceState<T> {
  /** The current entity being viewed/edited, or null if none selected */
  current: T | null;
  /** Whether any edits have been made since loading/saving */
  isEdited: boolean;
  /** List of field names that have been modified */
  editedFields: string[];
  /** Validation errors keyed by field name */
  validationErrors: Record<string, string>;
}

/**
 * Creates initial state for a slice with standard properties.
 */
export function createInitialState<T>(): BaseSliceState<T> {
  return {
    current: null,
    isEdited: false,
    editedFields: [],
    validationErrors: {},
  };
}

// =============================================================================
// EDIT TRACKING UTILITIES
// =============================================================================

/**
 * Helper to mark a field as edited in the state.
 * Adds the field to editedFields if not already present and sets isEdited to true.
 */
export function addEditedField<S extends { isEdited: boolean; editedFields: string[] }>(
  state: Draft<S>,
  fieldName: string
): void {
  if (!state.editedFields.includes(fieldName)) {
    state.editedFields.push(fieldName);
  }
  state.isEdited = true;
}

/**
 * Type for reducers that work with edit-tracking state.
 */
type EditTrackingState = {
  isEdited: boolean;
  editedFields: string[];
};

/**
 * Creates standard edit tracking reducers for a slice.
 * 
 * @returns Object containing reducer functions for edit tracking
 * 
 * @example
 * const editReducers = createEditTrackingReducers<WriteoffSliceState>();
 * 
 * const writeoffSlice = createSlice({
 *   name: 'writeoff',
 *   initialState,
 *   reducers: {
 *     ...editReducers,
 *     // other reducers
 *   }
 * });
 */
export function createEditTrackingReducers<S extends EditTrackingState>() {
  return {
    /**
     * Mark a specific field as edited
     */
    markFieldAsEdited: ((state, action: PayloadAction<string>) => {
      addEditedField(state, action.payload);
    }) as CaseReducer<S, PayloadAction<string>>,

    /**
     * Clear all edit tracking (after save or discard)
     */
    clearEditedFields: ((state) => {
      state.isEdited = false;
      state.editedFields = [];
    }) as CaseReducer<S>,

    /**
     * Mark as saved - clears edit tracking
     */
    markAsSaved: ((state) => {
      state.isEdited = false;
      state.editedFields = [];
    }) as CaseReducer<S>,

    /**
     * Discard changes - clears edit tracking (alias for clearEditedFields)
     */
    discardChanges: ((state) => {
      state.isEdited = false;
      state.editedFields = [];
    }) as CaseReducer<S>,
  };
}

// =============================================================================
// VALIDATION ERROR UTILITIES
// =============================================================================

type ValidationState = {
  validationErrors: Record<string, string>;
};

/**
 * Creates standard validation error reducers for a slice.
 */
export function createValidationReducers<S extends ValidationState>() {
  return {
    /**
     * Set a validation error for a field
     */
    setValidationError: ((state, action: PayloadAction<{ field: string; message: string }>) => {
      state.validationErrors[action.payload.field] = action.payload.message;
    }) as CaseReducer<S, PayloadAction<{ field: string; message: string }>>,

    /**
     * Clear a validation error for a field
     */
    clearValidationError: ((state, action: PayloadAction<string>) => {
      delete state.validationErrors[action.payload];
    }) as CaseReducer<S, PayloadAction<string>>,

    /**
     * Clear all validation errors
     */
    clearAllValidationErrors: ((state) => {
      state.validationErrors = {};
    }) as CaseReducer<S>,

    /**
     * Set multiple validation errors at once
     */
    setValidationErrors: ((state, action: PayloadAction<Record<string, string>>) => {
      state.validationErrors = { ...state.validationErrors, ...action.payload };
    }) as CaseReducer<S, PayloadAction<Record<string, string>>>,
  };
}

// =============================================================================
// SESSION STORAGE BACKUP MANAGER
// =============================================================================

/**
 * Creates a backup manager for persisting slice state to SessionStorage.
 * Useful for HMR (Hot Module Replacement) recovery during development.
 * 
 * @param storageKey - The key to use in SessionStorage
 * 
 * @example
 * const backupManager = createBackupManager<WriteoffSliceState>('writeoff-backup');
 * 
 * // In a reducer:
 * backupManager.persist(state);
 * 
 * // To restore:
 * const restored = backupManager.restore();
 */
export function createBackupManager<S>(storageKey: string) {
  return {
    /**
     * Persist state to SessionStorage
     */
    persist: (state: S): void => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.warn(`Failed to persist state to SessionStorage (${storageKey}):`, error);
      }
    },

    /**
     * Restore state from SessionStorage
     */
    restore: (): S | null => {
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          return JSON.parse(stored) as S;
        }
      } catch (error) {
        console.warn(`Failed to restore state from SessionStorage (${storageKey}):`, error);
      }
      return null;
    },

    /**
     * Clear backup from SessionStorage
     */
    clear: (): void => {
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.warn(`Failed to clear SessionStorage (${storageKey}):`, error);
      }
    },

    /**
     * Check if backup exists
     */
    exists: (): boolean => {
      return sessionStorage.getItem(storageKey) !== null;
    },
  };
}

/**
 * Creates reducers for backup/restore functionality.
 */
export function createBackupReducers<S>(storageKey: string, initialState: S) {
  const manager = createBackupManager<S>(storageKey);

  return {
    /**
     * Restore state from SessionStorage backup
     */
    restoreFromBackup: ((state) => {
      const backup = manager.restore();
      if (backup) {
        return backup as Draft<S>;
      }
      return state;
    }) as CaseReducer<S>,

    /**
     * Clear the SessionStorage backup
     */
    clearBackup: ((_state) => {
      manager.clear();
    }) as CaseReducer<S>,

    /**
     * Force reset to initial state and clear backup
     */
    forceReset: ((_state) => {
      manager.clear();
      return initialState as Draft<S>;
    }) as CaseReducer<S>,

    /** The backup manager instance for use in other reducers */
    _manager: manager,
  };
}

// =============================================================================
// ARRAY ITEM REDUCERS
// =============================================================================

/**
 * Interface for items that have an id property.
 */
export interface Identifiable {
  id: string;
}

/**
 * Creates generic reducers for managing array items within an entity.
 * Handles add, update, and remove operations with edit tracking.
 * 
 * @param arrayPath - The path to the array in the current entity (e.g., 'notes', 'approvals')
 * @param fieldName - The field name for edit tracking (e.g., 'notes', 'approvals')
 * 
 * @example
 * const notesReducers = createArrayItemReducers<WriteoffSliceState, WriteoffNote>(
 *   'notes',
 *   'notes'
 * );
 */
export function createArrayItemReducers<
  S extends { current: { [key: string]: unknown } | null; isEdited: boolean; editedFields: string[] },
  T extends Identifiable
>(arrayPath: string, fieldName: string) {
  type EntityWithArray = S['current'] & { [key: string]: T[] | undefined };

  return {
    /**
     * Add an item to the array
     */
    add: ((state, action: PayloadAction<T>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        if (!entity[arrayPath]) {
          (entity as Record<string, T[]>)[arrayPath] = [];
        }
        (entity[arrayPath] as T[]).push(action.payload);
        addEditedField(state, fieldName);
      }
    }) as CaseReducer<S, PayloadAction<T>>,

    /**
     * Update an item in the array by id
     */
    update: ((state, action: PayloadAction<T>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        const items = entity[arrayPath] as T[] | undefined;
        if (items) {
          const idx = items.findIndex((item) => item.id === action.payload.id);
          if (idx !== -1) {
            items[idx] = action.payload;
            addEditedField(state, fieldName);
          }
        }
      }
    }) as CaseReducer<S, PayloadAction<T>>,

    /**
     * Remove an item from the array by id
     */
    remove: ((state, action: PayloadAction<string>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        const items = entity[arrayPath] as T[] | undefined;
        if (items) {
          (entity as Record<string, T[]>)[arrayPath] = items.filter(
            (item) => item.id !== action.payload
          );
          addEditedField(state, fieldName);
        }
      }
    }) as CaseReducer<S, PayloadAction<string>>,

    /**
     * Set the entire array (replace all items)
     */
    set: ((state, action: PayloadAction<T[]>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        (entity as Record<string, T[]>)[arrayPath] = action.payload;
        addEditedField(state, fieldName);
      }
    }) as CaseReducer<S, PayloadAction<T[]>>,
  };
}

// =============================================================================
// PRIORITY-BASED ORDERING REDUCERS
// =============================================================================

/**
 * Interface for items that have priority ordering.
 */
export interface Prioritizable extends Identifiable {
  priority?: number;
}

/**
 * Interface for items that have priority and isPrimary flag (like procedures).
 */
export interface PrioritizableWithPrimary extends Prioritizable {
  isPrimary?: boolean;
}

/**
 * Creates reducers for managing priority-ordered arrays.
 * Handles moveUp, moveDown, and remove with automatic resequencing.
 * 
 * @param arrayPath - The path to the array in the current entity
 * @param fieldName - The field name for edit tracking
 * @param options - Optional configuration
 * 
 * @example
 * const procedureOrderReducers = createPriorityReducers<EncounterSliceState, ProcedureType>(
 *   'procedures',
 *   'procedures',
 *   { updateIsPrimary: true }
 * );
 */
export function createPriorityReducers<
  S extends { current: { [key: string]: unknown } | null; isEdited: boolean; editedFields: string[] },
  T extends Prioritizable
>(
  arrayPath: string,
  fieldName: string,
  options?: { updateIsPrimary?: boolean }
) {
  type EntityWithArray = S['current'] & { [key: string]: T[] | undefined };

  const shouldUpdateIsPrimary = options?.updateIsPrimary ?? false;

  const updatePrimaryFlag = (item: T, newPriority: number): T => {
    if (shouldUpdateIsPrimary && 'isPrimary' in item) {
      return { ...item, priority: newPriority, isPrimary: newPriority === 1 };
    }
    return { ...item, priority: newPriority };
  };

  return {
    /**
     * Move an item up in priority (lower priority number = higher in list)
     */
    moveUp: ((state, action: PayloadAction<string>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        const items = entity[arrayPath] as T[] | undefined;
        if (!items) return;

        // Sort by priority to get correct order
        const sortedItems = [...items].sort((a, b) => (a.priority || 0) - (b.priority || 0));
        const itemIndex = sortedItems.findIndex((item) => item.id === action.payload);

        if (itemIndex > 0) {
          const currentItem = sortedItems[itemIndex];
          const previousItem = sortedItems[itemIndex - 1];

          // Swap priorities
          const tempPriority = currentItem.priority;
          const updatedCurrent = updatePrimaryFlag(currentItem, previousItem.priority || 0);
          const updatedPrevious = updatePrimaryFlag(previousItem, tempPriority || 0);

          // Update original array
          const currentOriginalIndex = items.findIndex((item) => item.id === currentItem.id);
          const previousOriginalIndex = items.findIndex((item) => item.id === previousItem.id);

          if (currentOriginalIndex !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (items as any[])[currentOriginalIndex] = updatedCurrent;
          }
          if (previousOriginalIndex !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (items as any[])[previousOriginalIndex] = updatedPrevious;
          }

          addEditedField(state, fieldName);
        }
      }
    }) as CaseReducer<S, PayloadAction<string>>,

    /**
     * Move an item down in priority (higher priority number = lower in list)
     */
    moveDown: ((state, action: PayloadAction<string>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        const items = entity[arrayPath] as T[] | undefined;
        if (!items) return;

        // Sort by priority to get correct order
        const sortedItems = [...items].sort((a, b) => (a.priority || 0) - (b.priority || 0));
        const itemIndex = sortedItems.findIndex((item) => item.id === action.payload);

        if (itemIndex >= 0 && itemIndex < sortedItems.length - 1) {
          const currentItem = sortedItems[itemIndex];
          const nextItem = sortedItems[itemIndex + 1];

          // Swap priorities
          const tempPriority = currentItem.priority;
          const updatedCurrent = updatePrimaryFlag(currentItem, nextItem.priority || 0);
          const updatedNext = updatePrimaryFlag(nextItem, tempPriority || 0);

          // Update original array
          const currentOriginalIndex = items.findIndex((item) => item.id === currentItem.id);
          const nextOriginalIndex = items.findIndex((item) => item.id === nextItem.id);

          if (currentOriginalIndex !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (items as any[])[currentOriginalIndex] = updatedCurrent;
          }
          if (nextOriginalIndex !== -1) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (items as any[])[nextOriginalIndex] = updatedNext;
          }

          addEditedField(state, fieldName);
        }
      }
    }) as CaseReducer<S, PayloadAction<string>>,

    /**
     * Remove an item and resequence priorities
     */
    removeWithResequence: ((state, action: PayloadAction<string>) => {
      if (state.current) {
        const entity = state.current as EntityWithArray;
        const items = entity[arrayPath] as T[] | undefined;
        if (!items) return;

        const itemToRemove = items.find((item) => item.id === action.payload);
        const removedPriority = itemToRemove?.priority || 0;

        // Remove the item
        const filtered = items.filter((item) => item.id !== action.payload);

        // Resequence priorities for items that had higher priorities
        const resequenced = filtered.map((item) => {
          if (item.priority && item.priority > removedPriority) {
            return updatePrimaryFlag(item, item.priority - 1);
          }
          return item;
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (entity as Record<string, any>)[arrayPath] = resequenced;
        addEditedField(state, fieldName);
      }
    }) as CaseReducer<S, PayloadAction<string>>,
  };
}

// =============================================================================
// SELECTOR UTILITIES
// =============================================================================

import { createSelector } from '@reduxjs/toolkit';

/**
 * Creates a set of standard selectors for a slice.
 * 
 * @param sliceName - The name of the slice in the root state
 * 
 * @example
 * const writeoffSelectors = createStandardSelectors<RootState, WriteoffSliceState>('writeoff');
 * // writeoffSelectors.selectCurrent, selectIsEdited, selectEditedFields, etc.
 */
export function createStandardSelectors<
  RootState,
  S extends BaseSliceState<unknown>
>(sliceName: keyof RootState) {
  const selectSlice = (state: RootState) => state[sliceName] as S;

  return {
    /** Select the slice state */
    selectSlice,

    /** Select the current entity */
    selectCurrent: createSelector(selectSlice, (slice) => slice.current),

    /** Select whether edits have been made */
    selectIsEdited: createSelector(selectSlice, (slice) => slice.isEdited),

    /** Select the list of edited fields */
    selectEditedFields: createSelector(selectSlice, (slice) => slice.editedFields),

    /** Select validation errors */
    selectValidationErrors: createSelector(selectSlice, (slice) => slice.validationErrors),

    /** Select whether a specific field has been edited */
    selectIsFieldEdited: (fieldName: string) =>
      createSelector(selectSlice, (slice) => slice.editedFields.includes(fieldName)),

    /** Select validation error for a specific field */
    selectValidationError: (fieldName: string) =>
      createSelector(selectSlice, (slice) => slice.validationErrors[fieldName]),

    /** Select whether current entity exists */
    selectHasCurrent: createSelector(selectSlice, (slice) => slice.current !== null),
  };
}

// =============================================================================
// TYPE HELPERS
// =============================================================================

/**
 * Helper type to extract the entity type from a BaseSliceState
 */
export type EntityType<S> = S extends BaseSliceState<infer T> ? T : never;

/**
 * Helper type for creating typed action payloads for partial updates
 */
export type PartialUpdate<T> = { id: string; updates: Partial<T> };
